import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import DataTable from "@/components/shared/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ConfirmDialog from "@/components/shared/ConfirmDialog";
import api, { extractApiError } from "@/lib/api";
import { Users, Plus, Search, RefreshCw, X, MoreVertical, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import OnboardCustomerModal from "@/components/shared/OnboardCustomerModal";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function valueOrNA(value) {
  const normalized = String(value || "").trim();
  return normalized || "NA";
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getUserId(user) {
  return user?.id || user?.user_id || null;
}

export default function CustomerPage() {
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [customers, setCustomers] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // View
  const [viewOpen, setViewOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");

  // Edit
  const [editOpen, setEditOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [editFormData, setEditFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    state: "",
    city: "",
    district: "",
    pincode: "",
    address: "",
    blood_group: "",
    emergency_contact: "",
    role: "",
    is_active: true,
    password: "",
  });

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        page,
        page_size: pageSize,
        org_type: "CUSTOMER",
      };

      if (q.trim()) params.search = q.trim();
      if (activeFilter !== "all") params.is_active = activeFilter === "true";

      const { data } = await api.get("/users", { params });
      setCustomers(Array.isArray(data?.data) ? data.data : []);
      setMeta(
        data?.meta || {
          total: 0,
          page,
          page_size: pageSize,
          pages: 1,
        },
      );
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message || "Failed to load customers.");
      setCustomers([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [q, activeFilter, page, pageSize]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleOnboardSuccess = useCallback(
    (result) => {
      toast.success("Customer onboarded", {
        description: `Organisation ${result?.org_id ?? ""} created successfully.`,
      });
      fetchCustomers();
    },
    [fetchCustomers],
  );

  const hasAnyFilter = useMemo(() => {
    return !!q.trim() || activeFilter !== "all";
  }, [q, activeFilter]);

  const resetFilters = () => {
    setQ("");
    setActiveFilter("all");
    setPage(1);
  };

  const onChangeSearch = (value) => {
    setQ(value);
    setPage(1);
  };

  const handleViewUser = async (userId) => {
    if (!userId) return;
    setViewOpen(true);
    setViewLoading(true);
    setViewError("");
    setViewUser(null);
    try {
      const { data } = await api.get(`/users/${userId}`);
      setViewUser(data?.data || data || null);
    } catch (err) {
      const { message } = extractApiError(err);
      setViewError(message || "Failed to load customer details.");
    } finally {
      setViewLoading(false);
    }
  };

  const handleEditUser = async (userId) => {
    if (!userId) return;
    setEditOpen(true);
    setEditUserId(userId);
    setEditLoading(true);
    setEditError("");
    setEditFieldErrors({});
    try {
      const { data } = await api.get(`/users/${userId}`);
      const u = data?.data || data || {};
      setEditFormData({
        first_name: u.first_name || "",
        last_name: u.last_name || "",
        phone_number: u.phone_number || "",
        state: u.state || "",
        city: u.city || "",
        district: u.district || "",
        pincode: u.pincode || "",
        address: u.address || "",
        blood_group: u.blood_group || "",
        emergency_contact: u.emergency_contact || "",
        role: u.role || "",
        is_active: u.is_active ?? true,
        password: "",
      });
    } catch (err) {
      const { message } = extractApiError(err);
      setEditError(message || "Failed to load customer.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editUserId) return;

    if (editFormData.password.trim() && editFormData.password.trim().length < 8) {
      setEditError("Password must be at least 8 characters.");
      return;
    }

    setEditSubmitting(true);
    setEditError("");
    setEditFieldErrors({});
    try {
      const payload = {
        first_name: editFormData.first_name.trim() || undefined,
        last_name: editFormData.last_name.trim() || undefined,
        phone_number: editFormData.phone_number.trim() || undefined,
        state: editFormData.state.trim() || undefined,
        city: editFormData.city.trim() || undefined,
        district: editFormData.district.trim() || undefined,
        pincode: editFormData.pincode.trim() || undefined,
        address: editFormData.address.trim() || undefined,
        blood_group: editFormData.blood_group.trim() || undefined,
        emergency_contact: editFormData.emergency_contact.trim() || undefined,
        role: editFormData.role.trim() || undefined,
        is_active: !!editFormData.is_active,
      };

      if (editFormData.password.trim()) {
        payload.password = editFormData.password.trim();
      }

      await api.patch(`/users/${editUserId}`, payload);
      toast.success("Customer updated successfully.");
      setEditOpen(false);
      await fetchCustomers();
    } catch (err) {
      const { message, details } = extractApiError(err);
      if (Array.isArray(details?.fields)) {
        const map = {};
        details.fields.forEach((f) => {
          if (f?.field) map[f.field] = f.message || "Invalid value";
        });
        setEditFieldErrors(map);
      }
      setEditError(message || "Failed to update customer.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget?.id) return;
    setDeleteSubmitting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Customer removed successfully.");
      setDeleteTarget(null);
      await fetchCustomers();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to delete customer.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-page">
      <PageHeader
        title="Customers"
        subtitle="Manage and onboard customer accounts and their vehicles."
        actions={
          <Button
            className="bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => setShowOnboardModal(true)}
          >
            <Plus size={14} className="mr-1.5" /> Onboard Customer
          </Button>
        }
      />

      <div className="grid gap-3 md:grid-cols-[1fr_auto_auto_auto_auto]">
        <div className="relative">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            value={q}
            onChange={(e) => onChangeSearch(e.target.value)}
            placeholder="Search by name, email, phone, org..."
            className="h-11 rounded-xl border-slate-200 bg-white pl-10"
          />
        </div>

        <Select
          value={activeFilter}
          onValueChange={(value) => {
            setActiveFilter(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-11 min-w-[150px] rounded-xl border-slate-200 bg-white">
            <SelectValue placeholder="Active status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={String(pageSize)}
          onValueChange={(value) => {
            setPageSize(Number(value));
            setPage(1);
          }}
        >
          <SelectTrigger className="h-11 min-w-[120px] rounded-xl border-slate-200 bg-white">
            <SelectValue placeholder="Page size" />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasAnyFilter && (
          <Button variant="ghost" className="h-11" onClick={resetFilters}>
            <X size={14} className="mr-1.5" /> Clear
          </Button>
        )}

        <Button
          variant="outline"
          className="h-11 rounded-xl border-slate-200"
          onClick={fetchCustomers}
        >
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      <SectionCard padded={false} className="fix-data-table">

        <DataTable
          data={customers}
          rowKey={(u, idx) => u.id || u.user_id || `${u.email || "customer"}-${idx}`}
          onRowClick={(u) => handleViewUser(getUserId(u))}
          rowClassName="text-sm transition-colors hover:bg-slate-50/50"
          loading={loading}
          loadingMessage="Loading customers..."
          emptyState={
            <EmptyState
              icon={Users}
              title="No customers found"
              description="Try changing your search or filters, or onboard a new customer."
              action={
                <Button variant="outline" onClick={() => setShowOnboardModal(true)}>
                  <Plus size={14} className="mr-1.5" /> Onboard Customer
                </Button>
              }
            />
          }
          headClassName="font-bold"
          headerCellClassName="py-4"
          defaultCellClassName="py-4"
          bodyClassName="divide-y divide-slate-100"
          columns={[
            {
              key: "name",
              label: "Name",
              cellClassName: "font-semibold",
              render: (u) => {
                const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim();
                return <div className="text-sm text-slate-900">{fullName || "-"}</div>;
              },
            },
            { key: "username", label: "Username", render: (u) => valueOrNA(u.username) },
            { key: "email", label: "Email", render: (u) => valueOrNA(u.email) },
            { key: "phone", label: "Phone Number", render: (u) => valueOrNA(u.phone_number) },
            { key: "state", label: "State", render: (u) => valueOrNA(u.state) },
            { key: "city", label: "City", render: (u) => valueOrNA(u.city) },
            {
              key: "is_active",
              label: "Active",
              render: (u) => (
                <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-[11px] font-bold ${
                  u.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                }`}>
                  {u.is_active ? "Active" : "Inactive"}
                </span>
              ),
            },
            { key: "created_at", label: "Created At", render: (u) => formatDateTime(u.created_at) },
            {
              key: "actions",
              label: "Actions",
              className: "text-right",
              cellClassName: "text-right",
              render: (u) => {
                const userId = getUserId(u);
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        data-row-click-ignore="true"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" data-row-click-ignore="true" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem
                        data-row-click-ignore="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewUser(userId);
                        }}
                      >
                        <Eye size={14} /> View
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-row-click-ignore="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditUser(userId);
                        }}
                      >
                        <Pencil size={14} /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-row-click-ignore="true"
                        className="text-rose-600 focus:text-rose-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({
                            id: userId,
                            name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "this customer",
                          });
                        }}
                      >
                        <Trash2 size={14} /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              },
            },
          ]}
        />

        <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
          <Button
            variant="outline"
            className="border-slate-200"
            disabled={loading || page <= 1}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            className="border-slate-200"
            disabled={loading || page >= (meta.pages || 1)}
            onClick={() => setPage((prev) => Math.min(meta.pages || 1, prev + 1))}
          >
            Next
          </Button>
        </div>
      </SectionCard>

      <OnboardCustomerModal
        isOpen={showOnboardModal}
        onClose={() => setShowOnboardModal(false)}
        onSuccess={handleOnboardSuccess}
      />

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-xl rounded-2xl">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>

          {viewLoading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : viewError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{viewError}</div>
          ) : viewUser ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <p><span className="font-semibold">Name:</span> {`${viewUser.first_name || ""} ${viewUser.last_name || ""}`.trim() || "-"}</p>
              <p><span className="font-semibold">Username:</span> {valueOrNA(viewUser.username)}</p>
              <p><span className="font-semibold">Email:</span> {valueOrNA(viewUser.email)}</p>
              <p><span className="font-semibold">Phone:</span> {valueOrNA(viewUser.phone_number)}</p>
              <p><span className="font-semibold">State:</span> {valueOrNA(viewUser.state)}</p>
              <p><span className="font-semibold">City:</span> {valueOrNA(viewUser.city)}</p>
              <p><span className="font-semibold">District:</span> {valueOrNA(viewUser.district)}</p>
              <p><span className="font-semibold">Pincode:</span> {valueOrNA(viewUser.pincode)}</p>
              <p><span className="font-semibold">Active:</span> {viewUser.is_active ? "Active" : "Inactive"}</p>
              <p><span className="font-semibold">Created:</span> {formatDateTime(viewUser.created_at)}</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>

          {editLoading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[
                  ["first_name", "First Name"],
                  ["last_name", "Last Name"],
                  ["phone_number", "Phone Number"],
                  ["role", "Role"],
                  ["state", "State"],
                  ["city", "City"],
                  ["district", "District"],
                  ["pincode", "Pincode"],
                  ["address", "Address"],
                  ["blood_group", "Blood Group"],
                  ["emergency_contact", "Emergency Contact"],
                  ["password", "Reset Password"],
                ].map(([key, label]) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</Label>
                    <Input
                      type={key === "password" ? "password" : "text"}
                      value={editFormData[key]}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEditFormData((prev) => ({ ...prev, [key]: value }));
                        setEditFieldErrors((prev) => {
                          if (!prev?.[key]) return prev;
                          const copy = { ...prev };
                          delete copy[key];
                          return copy;
                        });
                      }}
                    />
                    {editFieldErrors[key] && <p className="text-xs text-rose-600">{editFieldErrors[key]}</p>}
                  </div>
                ))}
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Active</Label>
                <Select
                  value={editFormData.is_active ? "true" : "false"}
                  onValueChange={(value) => setEditFormData((prev) => ({ ...prev, is_active: value === "true" }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editError && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{editError}</div>}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSubmitting}>Cancel</Button>
                <Button onClick={handleUpdateUser} disabled={editSubmitting}>
                  {editSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete customer?"
        description={`This will remove ${deleteTarget?.name || "this customer"}. This action is soft-delete on the backend.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={deleteSubmitting}
        variant="danger"
        onConfirm={handleDeleteUser}
      />
    </div>
  );
}
